import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import { useAsync } from 'react-use';
import type { EntityDescriptionUiOptions } from './schema';

type Props = FieldExtensionComponentProps<
  string | undefined,
  EntityDescriptionUiOptions
>;

/** Read-only field that shows the catalog description of another field's entity. */
export const EntityDescription = ({ uiSchema, formContext }: Props) => {
  const catalogApi = useApi(catalogApiRef);
  const entityField = uiSchema?.['ui:options']?.entityField;
  const entityRef = entityField
    ? (formContext?.formData?.[entityField] as string | undefined)
    : undefined;

  const { value, loading, error } = useAsync(async () => {
    if (!entityRef) {
      return undefined;
    }
    const entity = await catalogApi.getEntityByRef(entityRef);
    return entity?.metadata.description ?? '';
  }, [catalogApi, entityRef]);

  if (!entityField) {
    return (
      <Alert severity="warning">
        EntityDescription needs <code>ui:options.entityField</code>.
      </Alert>
    );
  }

  if (!entityRef) {
    return (
      <Typography variant="body2" color="textSecondary">
        Select an entity in “{entityField}” to see its description.
      </Typography>
    );
  }

  if (loading) {
    return null;
  }

  if (error) {
    return <Alert severity="error">Failed to load {entityRef}.</Alert>;
  }

  if (!value) {
    return (
      <Typography variant="body2" color="textSecondary">
        No description found for {entityRef}.
      </Typography>
    );
  }

  return (
    <Alert severity="info" icon={false}>
      <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
        {value}
      </Typography>
    </Alert>
  );
};
